import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { CalendarIcon, Check, ChevronsUpDown, Loader2, X } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { ENTITIES as MASTER_ENTITIES } from '@/data/financial-systems';
import { useApp } from '@/contexts/AppContext';
import { getAllSalesTransactions } from '@/api/services/salesTransactionService';
import type { SalesTransactionApi } from '@/api/models/salesTransaction';

// Transform entities from master data
const ENTITIES = (MASTER_ENTITIES || []).map(entity => ({
  id: entity.id,
  name: entity.name + (entity.isHeadOffice ? ' (HQ)' : '')
}));

// Check if multi-entity system (more than 1 entity)
const IS_MULTI_ENTITY = ENTITIES.length > 1;

// Revenue transaction types for scenario transactions
const REVENUE_TRANSACTION_TYPES = [
  { id: 'new-customer', name: 'New Customer' },
  { id: 'new-project', name: 'New Project' },
  { id: 'one-off-sale', name: 'One-Off Sale' },
  { id: 'other', name: 'Other' },
];

// Demo-mode transactions for adjustment dropdown (invoices, credit notes, cashbook items)
const TRANSACTIONS = [
  { id: 'inv-001', type: 'Invoice', customer: 'Acme Corp', description: 'Monthly consulting services', amount: 45000, dueDate: '2026-02-15' },
  { id: 'inv-002', type: 'Invoice', customer: 'TechStart Ltd', description: 'Software license renewal', amount: 28000, dueDate: '2026-02-20' },
  { id: 'inv-003', type: 'Invoice', customer: 'Global Industries', description: 'Q1 project delivery', amount: 67000, dueDate: '2026-03-01' },
  { id: 'cn-001', type: 'Credit Note', customer: 'Innovate Solutions', description: 'Overpayment refund', amount: -5000, dueDate: '2026-02-25' },
  { id: 'cb-001', type: 'Cashbook', customer: 'Enterprise Systems', description: 'Hardware supply', amount: 12000, dueDate: '2026-03-05' },
  { id: 'inv-004', type: 'Invoice', customer: 'Digital Dynamics', description: 'Annual support contract', amount: 89000, dueDate: '2026-03-15' },
];

interface AdjustRevenueByPercentFormProps {
  onSave: (data: any) => void;
  onCancel: () => void;
  onLivePreview?: (data: any) => void;
  initialData?: any;
}

export function AdjustRevenueByPercentForm({ onSave, onCancel, onLivePreview, initialData }: AdjustRevenueByPercentFormProps) {
  const isEditing = !!initialData;
  const [adjustmentPercent, setAdjustmentPercent] = useState<number>(initialData?.adjustmentPercent ?? 0);
  const [entity, setEntity] = useState<string>(
    initialData?.entity
      ? (ENTITIES.find(e => e.name === initialData.entity || e.id === initialData.entity)?.id ?? 'all')
      : 'all'
  );
  const [effectiveDate, setEffectiveDate] = useState<Date | undefined>(
    initialData?.effectiveDate ? new Date(initialData.effectiveDate) : undefined
  );
  const [reason, setReason] = useState<string>(initialData?.reason ?? '');
  const [confidence, setConfidence] = useState<'certain' | 'probable' | 'possible' | 'unlikely'>(initialData?.confidence ?? 'probable');
  const [effectiveDateOpen, setEffectiveDateOpen] = useState(false);

  // Mock calculation - in real app, would calculate based on actual revenue
  const estimatedCurrentRevenue = 1200000; // £1.2M annual
  const adjustmentAmount = (estimatedCurrentRevenue * adjustmentPercent) / 100;
  const newAnnualRevenue = estimatedCurrentRevenue + adjustmentAmount;
  const monthlyImpact = adjustmentAmount / 12;

  // Update live preview whenever values change
  React.useEffect(() => {
    if (onLivePreview) {
      const selectedEntity = entity === 'all' ? null : ENTITIES.find((e) => e.id === entity);
      onLivePreview({
        type: 'adjust-revenue-percent',
        entity: entity === 'all' ? 'All Entities' : (selectedEntity?.name || 'All Entities'),
        adjustmentPercent,
        effectiveDate,
        reason,
        confidence,
        adjustmentAmount,
        monthlyImpact,
        estimatedCurrentRevenue,
      });
    }
  }, [adjustmentPercent, entity, effectiveDate, reason, confidence, adjustmentAmount, monthlyImpact, estimatedCurrentRevenue, onLivePreview]);

  const handleSave = () => {
    const selectedEntity = entity === 'all' ? null : ENTITIES.find((e) => e.id === entity);

    onSave({
      type: 'adjust-revenue-percent',
      entity: entity === 'all' ? 'All Entities' : (selectedEntity?.name || 'All Entities'),
      adjustmentPercent,
      effectiveDate,
      reason,
      confidence,
      adjustmentAmount,
      monthlyImpact,
      estimatedCurrentRevenue,
    });
  };

  const isValid = effectiveDate;

  return (
    <div className="space-y-6 p-6 border rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{isEditing ? 'Edit Revenue Adjustment' : 'Adjust Revenue by %'}</h3>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-4">
        {/* Entity Filter - Only show if multi-entity */}
        {IS_MULTI_ENTITY && (
          <div className="space-y-2">
            <Label>Entity</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {ENTITIES.map((e) => (
                  <SelectItem key={e.id} value={e.id}>
                    {e.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Adjustment Percentage */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Revenue Adjustment</Label>
            <span className="text-sm font-medium">
              {adjustmentPercent > 0 ? '+' : ''}
              {adjustmentPercent}%
            </span>
          </div>
          <Slider
            value={[adjustmentPercent]}
            onValueChange={(value) => setAdjustmentPercent(value[0])}
            min={-30}
            max={30}
            step={1}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>-30% (decrease)</span>
            <span>0%</span>
            <span>+30% (increase)</span>
          </div>
        </div>

        {/* Effective Date */}
        <div className="space-y-2">
          <Label>Effective Date</Label>
          <Popover open={effectiveDateOpen} onOpenChange={setEffectiveDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !effectiveDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {effectiveDate ? format(effectiveDate, 'PPP') : 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={effectiveDate}
                onSelect={(date) => {
                  setEffectiveDate(date);
                  setEffectiveDateOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Confidence Level */}
        <div className="space-y-2">
          <Label>Confidence Level</Label>
          <Select value={confidence} onValueChange={(v) => setConfidence(v as typeof confidence)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="certain">Certain (100% - will definitely happen)</SelectItem>
              <SelectItem value="probable">Probable (75% - likely to happen)</SelectItem>
              <SelectItem value="possible">Possible (50% - might happen)</SelectItem>
              <SelectItem value="unlikely">Unlikely (25% - low probability)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            How confident are you this adjustment will occur?
          </p>
        </div>

        {/* Reason */}
        <div className="space-y-2">
          <Label>Reason (optional)</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Market expansion, price increase..."
          />
        </div>

        {/* Live Impact Calculations */}
        <div
          className={cn(
            'p-4 border rounded-lg space-y-2',
            adjustmentPercent >= 0
              ? 'bg-green-50 border-green-200'
              : 'bg-red-50 border-red-200'
          )}
        >
          <h4
            className={cn(
              'font-semibold text-sm',
              adjustmentPercent >= 0 ? 'text-green-900' : 'text-red-900'
            )}
          >
            Revenue Impact
          </h4>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Annual Revenue:</span>
              <span className="font-medium">£{estimatedCurrentRevenue.toLocaleString()}</span>
            </div>
            <div className="flex justify-between pt-2 border-t">
              <span className="font-semibold">Annual Revenue Change:</span>
              <span
                className={cn(
                  'font-semibold',
                  adjustmentPercent >= 0 ? 'text-green-700' : 'text-red-700'
                )}
              >
                {adjustmentPercent > 0 ? '+' : ''}£{Math.round(adjustmentAmount).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="font-semibold">Monthly Impact:</span>
              <span
                className={cn(
                  'font-semibold',
                  adjustmentPercent >= 0 ? 'text-green-700' : 'text-red-700'
                )}
              >
                {adjustmentPercent > 0 ? '+' : ''}£{Math.abs(Math.round(monthlyImpact)).toLocaleString()}/month
              </span>
            </div>
            {effectiveDate && (
              <div className="pt-2 border-t">
                <p className="text-xs">
                  💡 Impact starting {format(effectiveDate, 'MMMM yyyy')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!isValid}>
          {isEditing ? 'Update Adjustment' : 'Add Adjustment'}
        </Button>
      </div>
    </div>
  );
}

interface AdjustSpecificTransactionFormProps {
  onSave: (data: any) => void;
  onCancel: () => void;
  onLivePreview?: (data: any) => void;
  initialData?: any;
}

export function AdjustSpecificTransactionForm({ onSave, onCancel, onLivePreview, initialData }: AdjustSpecificTransactionFormProps) {
  const isEditing = !!initialData;
  const { mode } = useApp();
  const [transactionId, setTransactionId] = useState(initialData?.transactionId ?? '');
  const [newDueDate, setNewDueDate] = useState<Date | undefined>(
    initialData?.newDueDate ? new Date(initialData.newDueDate) : undefined
  );
  const [adjustedValue, setAdjustedValue] = useState<string>(
    initialData?.adjustedValue !== undefined && initialData?.adjustedValue !== null
      ? String(initialData.adjustedValue)
      : ''
  );
  const [reason, setReason] = useState(initialData?.reason ?? '');
  const [confidence, setConfidence] = useState<'certain' | 'probable' | 'possible' | 'unlikely'>(initialData?.confidence ?? 'probable');
  const [newDueDateOpen, setNewDueDateOpen] = useState(false);
  const [liveTransactions, setLiveTransactions] = useState<typeof TRANSACTIONS>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchTransactions = useCallback((search?: string) => {
    if (mode !== 'live') return;
    setIsLoading(true);
    getAllSalesTransactions({ outstandingOnly: true, limit: 100, search: search || undefined })
      .then((response) => {
        const mapped = response.salesTransactions.map((t: SalesTransactionApi) => ({
          id: String(t.id),
          type: t.transactionType || 'Invoice',
          customer: t.accountName || 'Unknown',
          description: t.transactionDescription || '',
          amount: t.gross ?? 0,
          dueDate: t.dueDate || t.transactionDate || '',
        }));
        setLiveTransactions(mapped);
      })
      .catch((err) => {
        console.error('Failed to fetch sales transactions:', err);
        setLiveTransactions([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [mode]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    if (mode !== 'live') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchTransactions(value);
    }, 300);
  }, [mode, fetchTransactions]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const transactions = mode === 'live' ? liveTransactions : TRANSACTIONS;

  useEffect(() => {
    if (initialData?.transactionId && transactions.length > 0) {
      const found = transactions.find(t => t.id === initialData.transactionId);
      if (!found) {
        console.warn('Edit mode: transactionId not found in transaction list', initialData.transactionId);
      }
    }
  }, [transactions, initialData]);

  const selectedTransaction = transactions.find((txn) => txn.id === transactionId);
  const originalDueDate = selectedTransaction ? new Date(selectedTransaction.dueDate) : null;
  
  // Calculate date change in days
  const dateChangeDays = originalDueDate && newDueDate 
    ? Math.round((newDueDate.getTime() - originalDueDate.getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  // Calculate value change
  const adjustedValueNum = adjustedValue ? parseFloat(adjustedValue) : null;
  const valueChange = adjustedValueNum !== null && selectedTransaction 
    ? adjustedValueNum - selectedTransaction.amount 
    : 0;

  // Update live preview whenever values change
  React.useEffect(() => {
    if (onLivePreview && selectedTransaction && (newDueDate || adjustedValue)) {
      onLivePreview({
        type: 'adjust-transaction',
        name: `Adjust ${selectedTransaction.customer}`,
        transactionId: selectedTransaction.id,
        customer: selectedTransaction.customer,
        originalAmount: selectedTransaction.amount,
        amount: adjustedValueNum !== null ? adjustedValueNum : selectedTransaction.amount,
        adjustedValue: adjustedValueNum !== null ? adjustedValueNum : selectedTransaction.amount,
        dateChangeDays,
        valueChange,
        reason,
        confidence,
      });
    } else if (onLivePreview) {
      onLivePreview(null);
    }
  }, [transactionId, newDueDate, adjustedValue, adjustedValueNum, dateChangeDays, valueChange, reason, confidence, selectedTransaction, onLivePreview]);

  const handleSave = () => {
    if (!selectedTransaction) return;

    onSave({
      type: 'adjust-transaction',
      name: `Adjust ${selectedTransaction.customer}`,
      transactionId: selectedTransaction.id,
      transactionType: selectedTransaction.type,
      customer: selectedTransaction.customer,
      originalAmount: selectedTransaction.amount,
      amount: adjustedValueNum !== null ? adjustedValueNum : selectedTransaction.amount,
      originalDueDate: selectedTransaction.dueDate,
      newDueDate: newDueDate || new Date(selectedTransaction.dueDate),
      adjustedValue: adjustedValueNum !== null ? adjustedValueNum : selectedTransaction.amount,
      dateChangeDays,
      valueChange,
      reason,
      confidence,
    });
  };

  const isValid = transactionId && (newDueDate || adjustedValue);

  return (
    <div className="space-y-6 p-6 border rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{isEditing ? 'Edit Transaction Adjustment' : 'Adjust Specific Transaction'}</h3>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-4">
        {/* Transaction Selection */}
        <div className="space-y-2">
          <Label>Transaction</Label>
          <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                role="combobox"
                aria-expanded={comboboxOpen}
                className="w-full justify-between font-normal"
              >
                {transactionId
                  ? (() => {
                      const txn = transactions.find((t) => t.id === transactionId);
                      return txn
                        ? `${txn.type} - ${txn.customer}${txn.description ? ` - ${txn.description}` : ''} - £${Math.abs(txn.amount).toLocaleString()}`
                        : 'Select transaction...';
                    })()
                  : 'Select transaction...'}
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder="Type to search transactions..."
                  value={searchTerm}
                  onValueChange={handleSearchChange}
                />
                <CommandList>
                  {isLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                      <span className="ml-2 text-sm text-muted-foreground">Loading transactions...</span>
                    </div>
                  ) : transactions.length === 0 ? (
                    <div className="py-6 text-center text-sm text-muted-foreground">
                      No transactions available
                    </div>
                  ) : (
                    <>
                      <CommandEmpty>No transactions found.</CommandEmpty>
                      <CommandGroup>
                        {transactions.map((txn) => (
                          <CommandItem
                            key={txn.id}
                            value={txn.id}
                            onSelect={(value) => {
                              setTransactionId(value === transactionId ? '' : value);
                              setComboboxOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                transactionId === txn.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            {txn.type} - {txn.customer}{txn.description ? ` - ${txn.description}` : ''} - £{Math.abs(txn.amount).toLocaleString()} (Due: {format(new Date(txn.dueDate), 'MMM dd, yyyy')})
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>
        </div>

        {/* Show transaction details when selected */}
        {selectedTransaction && (
          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Type:</span>
              <span className="font-medium">{selectedTransaction.type}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Customer:</span>
              <span className="font-medium">{selectedTransaction.customer}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Amount:</span>
              <span className="font-medium">£{selectedTransaction.amount.toLocaleString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Current Due Date:</span>
              <span className="font-medium">{format(new Date(selectedTransaction.dueDate), 'MMM dd, yyyy')}</span>
            </div>
          </div>
        )}

        {/* New Due Date */}
        <div className="space-y-2">
          <Label>New Due Date (optional)</Label>
          <Popover open={newDueDateOpen} onOpenChange={setNewDueDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !newDueDate && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {newDueDate ? format(newDueDate, 'PPP') : 'Keep current date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={newDueDate}
                onSelect={(date) => {
                  setNewDueDate(date);
                  setNewDueDateOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Adjusted Value */}
        <div className="space-y-2">
          <Label>Adjusted Value (optional)</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm">£</span>
            <Input
              type="number"
              value={adjustedValue}
              onChange={(e) => setAdjustedValue(e.target.value)}
              placeholder={selectedTransaction ? selectedTransaction.amount.toString() : "Keep current value"}
              className="flex-1"
            />
          </div>
          {adjustedValueNum !== null && selectedTransaction && (
            <p className="text-xs text-muted-foreground">
              Change: {valueChange >= 0 ? '+' : ''}£{valueChange.toLocaleString()}
            </p>
          )}
        </div>

        {/* Reason */}
        <div className="space-y-2">
          <Label>Reason (optional)</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Customer requested extension, negotiated discount..."
          />
        </div>

        {/* Confidence */}
        <div className="space-y-2">
          <Label>Confidence Level</Label>
          <Select value={confidence} onValueChange={(v) => setConfidence(v as typeof confidence)}>
            <SelectTrigger>
              <SelectValue placeholder="Select confidence..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="certain">Certain</SelectItem>
              <SelectItem value="probable">Probable</SelectItem>
              <SelectItem value="possible">Possible</SelectItem>
              <SelectItem value="unlikely">Unlikely</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            How confident are you this adjustment will occur?
          </p>
        </div>

        {/* Live Impact Calculations */}
        {selectedTransaction && (newDueDate || adjustedValue) && (
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-2">
            <h4 className="font-semibold text-sm text-blue-900">Transaction Impact</h4>
            <div className="space-y-1 text-sm">
              {newDueDate && dateChangeDays !== 0 && (
                <>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Date Change:</span>
                    <span className={cn(
                      "font-medium",
                      dateChangeDays > 0 ? "text-orange-700" : "text-green-700"
                    )}>
                      {dateChangeDays > 0 ? `+${dateChangeDays}` : dateChangeDays} days
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">New Date:</span>
                    <span className="font-medium">{format(newDueDate, 'MMM dd, yyyy')}</span>
                  </div>
                </>
              )}
              {adjustedValueNum !== null && valueChange !== 0 && (
                <>
                  <div className="flex justify-between pt-2 border-t border-blue-200">
                    <span className="text-muted-foreground">Value Change:</span>
                    <span className={cn(
                      "font-medium",
                      valueChange >= 0 ? "text-green-700" : "text-red-700"
                    )}>
                      {valueChange >= 0 ? '+' : ''}£{valueChange.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">New Value:</span>
                    <span className="font-medium">£{adjustedValueNum.toLocaleString()}</span>
                  </div>
                </>
              )}
              <div className="pt-2 border-t border-blue-200">
                <p className="text-xs text-blue-700">
                  💡 {newDueDate && dateChangeDays !== 0 ? `Date ${dateChangeDays > 0 ? 'delayed' : 'brought forward'}` : ''}
                  {newDueDate && dateChangeDays !== 0 && adjustedValueNum !== null && valueChange !== 0 ? ' and ' : ''}
                  {adjustedValueNum !== null && valueChange !== 0 ? `value ${valueChange >= 0 ? 'increased' : 'reduced'}` : ''}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!isValid}>
          {isEditing ? 'Update Adjustment' : 'Add Adjustment'}
        </Button>
      </div>
    </div>
  );
}

interface AddScenarioTransactionFormProps {
  onSave: (data: any) => void;
  onCancel: () => void;
  onLivePreview?: (data: any) => void;
  initialData?: any;
}

export function AddScenarioTransactionForm({ onSave, onCancel, onLivePreview, initialData }: AddScenarioTransactionFormProps) {
  const isEditing = !!initialData;
  const [transactionType, setTransactionType] = useState(
    initialData?.transactionType
      ? (REVENUE_TRANSACTION_TYPES.find(t => t.name === initialData.transactionType || t.id === initialData.transactionType)?.id ?? '')
      : ''
  );
  const [date, setDate] = useState<Date | undefined>(
    initialData?.date ? new Date(initialData.date) : undefined
  );
  const [amount, setAmount] = useState(initialData?.amount ?? 0);
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [confidence, setConfidence] = useState<'certain' | 'probable' | 'possible' | 'unlikely'>(initialData?.confidence ?? 'probable');
  const [reason, setReason] = useState(initialData?.reason ?? '');
  const [dateOpen, setDateOpen] = useState(false);

  // Update live preview whenever values change
  React.useEffect(() => {
    if (onLivePreview && amount > 0 && date) {
      onLivePreview({
        type: 'add-scenario-transaction',
        transactionType,
        date,
        amount,
        description,
        confidence,
        reason,
      });
    } else if (onLivePreview) {
      onLivePreview(null);
    }
  }, [transactionType, date, amount, description, confidence, reason, onLivePreview]);

  const handleSave = () => {
    onSave({
      type: 'add-scenario-transaction',
      transactionType,
      date,
      amount,
      description,
      confidence,
      reason,
    });
  };

  const isValid = transactionType && date && amount > 0 && description && confidence;

  return (
    <div className="space-y-6 p-6 border rounded-lg bg-white">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">{isEditing ? 'Edit Scenario Transaction' : 'Add Scenario Transaction'}</h3>
        <Button variant="ghost" size="sm" onClick={onCancel}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="space-y-4">
        {/* Transaction Type */}
        <div className="space-y-2">
          <Label>Transaction Type</Label>
          <Select value={transactionType} onValueChange={setTransactionType}>
            <SelectTrigger>
              <SelectValue placeholder="Select type..." />
            </SelectTrigger>
            <SelectContent>
              {REVENUE_TRANSACTION_TYPES.map((type) => (
                <SelectItem key={type.id} value={type.id}>
                  {type.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Date */}
        <div className="space-y-2">
          <Label>Expected Date</Label>
          <Popover open={dateOpen} onOpenChange={setDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={cn(
                  'w-full justify-start text-left font-normal',
                  !date && 'text-muted-foreground'
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date ? format(date, 'PPP') : 'Pick a date'}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <Calendar
                mode="single"
                selected={date}
                onSelect={(d) => {
                  setDate(d);
                  setDateOpen(false);
                }}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </div>

        {/* Amount */}
        <div className="space-y-2">
          <Label>Amount</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm">£</span>
            <Input
              type="number"
              value={amount || ''}
              onChange={(e) => setAmount(Number(e.target.value))}
              placeholder="0"
              className="flex-1"
            />
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label>Description</Label>
          <Input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="e.g., New customer contract, Dairy yogurt sales..."
          />
        </div>

        {/* Confidence */}
        <div className="space-y-2">
          <Label>Confidence Level</Label>
          <Select value={confidence} onValueChange={(v) => setConfidence(v as typeof confidence)}>
            <SelectTrigger>
              <SelectValue placeholder="Select confidence..." />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="certain">Certain</SelectItem>
              <SelectItem value="probable">Probable</SelectItem>
              <SelectItem value="possible">Possible</SelectItem>
              <SelectItem value="unlikely">Unlikely</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Reason */}
        <div className="space-y-2">
          <Label>Business Context (optional)</Label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g., Expanding into new market segment..."
          />
        </div>

        {/* Live Impact Calculations */}
        {amount > 0 && date && confidence && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-2">
            <h4 className="font-semibold text-sm text-green-900">Revenue Impact</h4>
            <div className="space-y-1 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Transaction Amount:</span>
                <span className="font-medium">£{amount.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Expected Date:</span>
                <span className="font-medium">{format(date, 'MMM dd, yyyy')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Confidence:</span>
                <span className="font-medium capitalize">{confidence}</span>
              </div>
              <div className="pt-2 border-t border-green-200">
                <p className="text-xs text-green-700">
                  💡 Anticipated revenue: +£{amount.toLocaleString()} in {format(date, 'MMMM yyyy')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={!isValid}>
          {isEditing ? 'Update Adjustment' : 'Add Adjustment'}
        </Button>
      </div>
    </div>
  );
}
